import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { resetUser } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONFIRM_TEXT = 'DELETE';

const DELETION_ITEMS = [
  'account and profile information',
  'goals and identity statements',
  'Future Self letters and daily actions',
  'gratitude entries',
  'private journal entries',
  'AI affirmations',
  'uploaded backgrounds and saved visualisations',
  'reminder settings and account history',
];

const DeleteAccountSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(0); // 0 closed | 1 first modal | 2 final modal
  const [confirmText, setConfirmText] = useState('');
  const [processing, setProcessing] = useState(false);
  const inFlight = useRef(false);
  const triggerRef = useRef(null);

  // Return focus to the trigger button when the dialog closes (after a cancel).
  // Only after a dialog was actually open (not on mount, which would scroll the page here).
  const prevStep = useRef(0);
  useEffect(() => {
    const wasOpen = prevStep.current !== 0;
    prevStep.current = step;
    if (step === 0 && wasOpen && triggerRef.current) {
      // Small timeout to let Radix finish its close animation
      const t = setTimeout(() => triggerRef.current?.focus({ preventScroll: true }), 50);
      return () => clearTimeout(t);
    }
  }, [step]);

  const closeAll = useCallback(() => {
    if (processing) return; // Do not allow closing mid-request
    setStep(0);
    setConfirmText('');
  }, [processing]);

  const performDeletion = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/users/me`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        toast.error('We could not delete your account. Please try again.');
        setProcessing(false);
        // Keep the in-flight lock for a short cooldown so a rapid double-click
        // after error can't trigger a second DELETE before React re-disables
        // the button via `processing`.
        setTimeout(() => { inFlight.current = false; }, 800);
        return;
      }

      // Success — sweep every client-side trace before redirecting.
      try {
        Object.keys(localStorage || {}).forEach((k) => {
          if (
            k === 'auth_token' ||
            k.startsWith('futureself_') ||
            k.startsWith('fs_')
          ) {
            localStorage.removeItem(k);
          }
        });
      } catch { /* ignore */ }
      try { sessionStorage.clear(); } catch { /* ignore */ }
      try { resetUser(); } catch { /* ignore */ }

      // Show final confirmation, then redirect and force a full reload so no
      // cached React state (user, premium, goals, journal, visualisations)
      // survives.
      toast.success('Your Future Self account has been permanently deleted.');
      // Small delay so the toast is visible before nav
      setTimeout(() => {
        window.location.replace('/login');
      }, 600);
    } catch {
      toast.error('We could not delete your account. Please try again.');
      setProcessing(false);
      setTimeout(() => { inFlight.current = false; }, 800);
    }
  };

  const canConfirm = confirmText.trim() === CONFIRM_TEXT && !processing;
  const isPremium = !!user?.is_premium;

  return (
    <>
      <section
        aria-label="Delete account"
        className="bg-[#252528] border border-white/5 rounded-[24px] p-6"
        data-testid="delete-account-section"
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-[#C97A6E]" strokeWidth={1.5} />
          <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">
            Delete Account
          </span>
        </div>
        <p className="text-[#B5B1AB] font-sans text-sm leading-relaxed mb-5">
          Permanently delete your Future Self account and associated personal data,
          including your goals, journal entries, gratitude entries, affirmations,
          saved visualisations and reminder settings.{' '}
          <span className="text-[#DCD3CA]">This action cannot be undone.</span>
        </p>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setStep(1)}
          className="rounded-full px-5 py-2.5 text-sm font-sans bg-transparent border border-[#C97A6E]/50 text-[#C97A6E] hover:bg-[#C97A6E]/10 hover:border-[#C97A6E] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C97A6E]/40 focus:ring-offset-2 focus:ring-offset-[#252528]"
          data-testid="delete-account-btn"
          aria-label="Delete my account"
        >
          Delete My Account
        </button>
      </section>

      {/* ---------- Step 1: Information & confirmation ---------- */}
      <Dialog open={step === 1} onOpenChange={(v) => !processing && setStep(v ? 1 : 0)}>
        <DialogContent
          className="bg-[#252528] border border-white/10 text-[#DCD3CA] sm:max-w-md rounded-[24px] max-h-[90vh] overflow-y-auto"
          onEscapeKeyDown={(e) => { if (processing) e.preventDefault(); }}
          onInteractOutside={(e) => { if (processing) e.preventDefault(); }}
          data-testid="delete-account-modal-step-1"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#DCD3CA]">
              Delete your account?
            </DialogTitle>
            <DialogDescription className="text-[#B5B1AB] font-sans text-sm leading-relaxed pt-2">
              Deletion will permanently remove:
            </DialogDescription>
          </DialogHeader>

          <ul className="list-disc pl-5 space-y-1 text-[#B5B1AB] font-sans text-sm leading-relaxed">
            {DELETION_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <p className="text-[#DCD3CA] font-sans text-sm pt-3 font-medium">
            This action cannot be undone.
          </p>

          {isPremium && (
            <p className="text-[#C6A75E] font-sans text-sm pt-1" data-testid="delete-account-premium-note">
              Your Premium subscription will be cancelled and will not renew.
            </p>
          )}

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep(0)}
              className="text-[#DCD3CA] hover:bg-white/5 rounded-full px-5 h-12 font-sans"
              data-testid="delete-account-keep-btn"
            >
              Keep My Account
            </Button>
            <Button
              type="button"
              onClick={() => setStep(2)}
              className="rounded-full bg-[#C97A6E] hover:bg-[#C97A6E]/90 text-[#1C1C1E] font-sans px-5 h-12"
              data-testid="delete-account-continue-btn"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Step 2: Type DELETE to confirm ---------- */}
      <Dialog open={step === 2} onOpenChange={(v) => !processing && setStep(v ? 2 : 0)}>
        <DialogContent
          className="bg-[#252528] border border-[#C97A6E]/30 text-[#DCD3CA] sm:max-w-md rounded-[24px]"
          onEscapeKeyDown={(e) => { if (processing) e.preventDefault(); }}
          onInteractOutside={(e) => { if (processing) e.preventDefault(); }}
          data-testid="delete-account-modal-step-2"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#DCD3CA]">
              Type DELETE to confirm
            </DialogTitle>
            <DialogDescription className="text-[#B5B1AB] font-sans text-sm leading-relaxed pt-2">
              To permanently delete your account, type{' '}
              <span className="font-mono text-[#DCD3CA]">DELETE</span> in the box below.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-2">
            <label htmlFor="delete-confirm-input" className="sr-only">
              Type DELETE to confirm account deletion
            </label>
            <Input
              id="delete-confirm-input"
              type="text"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={processing}
              className="bg-[#1C1C1E] border-white/10 text-[#DCD3CA] font-mono tracking-widest h-12 focus:border-[#C97A6E] focus:ring-[#C97A6E]/40"
              data-testid="delete-account-confirm-input"
              aria-describedby="delete-confirm-help"
            />
            <p id="delete-confirm-help" className="text-[#7A7670] text-xs font-sans pt-2">
              The button below stays disabled until the text matches exactly.
            </p>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="ghost"
              disabled={processing}
              onClick={() => { setConfirmText(''); setStep(1); }}
              className="text-[#DCD3CA] hover:bg-white/5 rounded-full px-5 h-12 font-sans"
              data-testid="delete-account-back-btn"
            >
              Back
            </Button>
            <Button
              type="button"
              disabled={!canConfirm}
              onClick={performDeletion}
              className="rounded-full bg-[#C97A6E] hover:bg-[#C97A6E]/90 text-[#1C1C1E] font-sans px-5 h-12 disabled:opacity-40"
              data-testid="delete-account-final-btn"
              aria-busy={processing}
            >
              {processing ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting account…
                </span>
              ) : (
                'Permanently Delete Account'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteAccountSection;
